// Pure helpers extracted from TeamRoleTree (PRODOPSAI-509).
//
// Lives in its own module so:
//   1. SonarCloud rule S7721 (helpers defined inside component render) cannot
//      fire — every grouping / filtering function is module-level.
//   2. US10 can unit-test them without rendering React.
//   3. The composition root stays under SonarCloud's cognitive-complexity
//      threshold (S3776 < 15).

import type {
  ITeamMembershipTreePerson,
  ITeamMembershipTreeTeam,
  IOrgGraphNode,
} from '@skystone/shared';

/**
 * Stable label for the "no region" bucket. Exported so test assertions and
 * the renderer reference the same string.
 *
 * NOTE: Use `UNASSIGNED_REGION_VALUE` (a sentinel string) as the filter
 * selector value — never this human-readable label. Fabric is free to name
 * a region literally "Unassigned region" and that would collide with the
 * filter logic if the value and the label were the same string
 * (Rabizard #662 finding 1).
 */
export const UNASSIGNED_REGION_LABEL = 'Unassigned region';

/**
 * Filter-selector value for the "no region" bucket. Picked so that it cannot
 * collide with any Fabric-supplied region name (regions are user-entered
 * free text, but starting with double underscores is reserved here for the
 * sentinel). Always paired with `UNASSIGNED_REGION_LABEL` for display.
 */
export const UNASSIGNED_REGION_VALUE = '__unassigned_region__';

/**
 * Shared CSS-grid column template for the per-team member table (PRODOPSAI-539)
 * so the column-header row (TeamRoleTreeNode) and the member rows
 * (TeamMemberRow) align: Name | Team role | AD/Entra title | actions.
 */
export const MEMBER_ROW_GRID =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1.5fr)_auto] items-center gap-3';

/**
 * A `{ value, label }` pair for the region filter dropdown. `value` is what
 * the page stores in the URL `?region=` param and forwards to
 * `filterTeamsByRegion`; `label` is what the user sees.
 */
export interface RegionOption {
  value: string;
  label: string;
}

export interface TeamRegionGroup {
  /** Display label (real region name, or UNASSIGNED_REGION_LABEL). */
  regionLabel: string;
  /** Teams in this region, alphabetical by name. */
  teams: ITeamMembershipTreeTeam[];
}

/**
 * Split the tree response into two slices:
 *   - `active`: non-archived teams, grouped by region (alphabetical, with
 *               the "Unassigned region" bucket appended last).
 *   - `archived`: archived teams in a flat list, alphabetical by name. The
 *                 caller decides whether to render them.
 *
 * Pure — does not mutate input arrays.
 */
export function groupTeamsByRegion(
  teams: readonly ITeamMembershipTreeTeam[],
): { active: TeamRegionGroup[]; archived: ITeamMembershipTreeTeam[] } {
  const archived: ITeamMembershipTreeTeam[] = [];
  const byRegion = new Map<string, ITeamMembershipTreeTeam[]>();
  let unassigned: ITeamMembershipTreeTeam[] | null = null;

  for (const entry of teams) {
    if (entry.team.archived === true) {
      archived.push(entry);
      continue;
    }
    const region = entry.team.regionName;
    if (region === null || region === undefined || region.trim() === '') {
      unassigned ??= [];
      unassigned.push(entry);
      continue;
    }
    const bucket = byRegion.get(region);
    if (bucket === undefined) {
      byRegion.set(region, [entry]);
    } else {
      bucket.push(entry);
    }
  }

  // Sort regions alphabetically, then teams inside each region.
  const regionNames = Array.from(byRegion.keys()).sort((a, b) => a.localeCompare(b));
  const active: TeamRegionGroup[] = regionNames.map((regionLabel) => {
    const teamsInRegion = (byRegion.get(regionLabel) ?? []).slice();
    teamsInRegion.sort((a, b) => a.team.name.localeCompare(b.team.name));
    return { regionLabel, teams: teamsInRegion };
  });

  if (unassigned !== null) {
    const teamsUnassigned = unassigned.slice();
    teamsUnassigned.sort((a, b) => a.team.name.localeCompare(b.team.name));
    active.push({ regionLabel: UNASSIGNED_REGION_LABEL, teams: teamsUnassigned });
  }

  archived.sort((a, b) => a.team.name.localeCompare(b.team.name));

  return { active, archived };
}

/**
 * Case-insensitive contains-match on the person's display name.
 * Empty queries match everything.
 */
function personMatches(person: ITeamMembershipTreePerson, needle: string): boolean {
  if (needle === '') return true;
  return person.displayName.toLowerCase().includes(needle);
}

/**
 * Filter the tree by a search query. Teams that contain at least one matching
 * person are kept (with only matching persons surviving inside each role
 * group). Teams with zero matches are dropped entirely.
 *
 * The "ancestor-of-a-match remains visible" rule from the US6 spec falls out
 * naturally: we keep the team + role group whenever any descendant person
 * matches, even if the role-group name itself does not.
 */
export function filterTreeBySearch(
  teams: readonly ITeamMembershipTreeTeam[],
  query: string,
): ITeamMembershipTreeTeam[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return teams.slice();

  const result: ITeamMembershipTreeTeam[] = [];
  for (const team of teams) {
    const filteredGroups = team.roleGroups
      .map((group) => ({
        ...group,
        persons: group.persons.filter((p) => personMatches(p, needle)),
      }))
      .filter((group) => group.persons.length > 0);

    if (filteredGroups.length === 0) continue;

    const matchedCount = filteredGroups.reduce((sum, g) => sum + g.persons.length, 0);
    result.push({
      team: team.team,
      memberCount: matchedCount,
      roleGroups: filteredGroups,
    });
  }
  return result;
}

/**
 * Filter teams down to a specific region. `null` / `''` means "no filter".
 * Special value `UNASSIGNED_REGION_VALUE` selects teams with no region.
 *
 * The sentinel is matched on the *value*, never on the display label, so a
 * Fabric region literally named "Unassigned region" still filters
 * correctly (Rabizard #662 finding 1).
 */
export function filterTeamsByRegion(
  teams: readonly ITeamMembershipTreeTeam[],
  region: string | null,
): ITeamMembershipTreeTeam[] {
  if (region === null || region.trim() === '') return teams.slice();
  if (region === UNASSIGNED_REGION_VALUE) {
    return teams.filter((entry) => {
      const r = entry.team.regionName;
      return r === null || r === undefined || r.trim() === '';
    });
  }
  return teams.filter((entry) => entry.team.regionName === region);
}

export interface PersonDisplayProps {
  /** Classes to apply to the displayName span (inactive => muted + strikethrough). */
  nameClassName: string;
  /** Tooltip content. `null` when none should be shown. */
  tooltipContent: string | null;
}

/**
 * Compute the visual props for a person row from its raw model fields.
 * Inactive persons get muted-foreground + strikethrough; the tooltip surfaces
 * the deactivation date when available.
 */
export function personDisplayProps(person: ITeamMembershipTreePerson): PersonDisplayProps {
  if (person.isActive === true) {
    return { nameClassName: 'text-foreground', tooltipContent: null };
  }
  const deactivatedDate = person.deactivatedAt === null
    ? null
    : person.deactivatedAt.substring(0, 10);
  const tooltipContent = deactivatedDate === null
    ? 'Departed per Entra sync'
    : `Departed ${deactivatedDate} per Entra sync`;
  return {
    nameClassName: 'text-muted-foreground line-through',
    tooltipContent,
  };
}

/**
 * PRODOPSAI-520: adapt a tree person into the `IOrgGraphNode` shape that
 * `PersonHoverCard` consumes. The card renders `displayName`, `jobTitle`,
 * `mail`, `isExternal`, and a separately-passed department label.
 *
 * The membership-scoped tree endpoint does NOT carry the Entra UPN, which has
 * two consequences:
 *   - `departmentId` / `managerUpn` / `unresolvedManagerName` / `isCurrentUser`
 *     are truly inert here — the card never reads them. The real department
 *     NAME is passed to the card via its `departmentLabel` prop (not
 *     `departmentId`).
 *   - `isExternal` IS rendered by the card, but external/guest detection needs
 *     the `#EXT#` UPN marker that this endpoint doesn't return, so it is set
 *     `false` deliberately: the "External" badge is intentionally not surfaced
 *     in the admin team tree. (If that becomes required, project the UPN /
 *     external flag onto the tree person and derive it here.)
 *
 * Defined at module scope (not inside the component) per S7721.
 */
export function toHoverCardNode(person: ITeamMembershipTreePerson): IOrgGraphNode {
  return {
    id: person.personId,
    displayName: person.displayName,
    jobTitle: person.jobTitle,
    departmentId: 'unassigned',
    mail: person.email,
    managerUpn: null,
    unresolvedManagerName: null,
    isCurrentUser: false,
    isExternal: false,
  };
}

/**
 * Collect distinct region names present in the team list as `{ value, label }`
 * pairs, alphabetical, with the unassigned-region sentinel appended last when
 * at least one team has no region. Used by both the admin page's and the
 * public Team Visualization page's region dropdowns (PRODOPSAI-532), so it
 * takes the team array directly.
 *
 * When `showArchived === true`, archived teams' regions are included so that
 * a region whose only teams are archived stays reachable in the dropdown
 * while the toggle is on (Rabizard #662 finding 4). When `showArchived` is
 * false (default), archived teams are excluded from the picker.
 */
export function listAvailableRegions(
  teams: readonly ITeamMembershipTreeTeam[] | null,
  showArchived = false,
): RegionOption[] {
  if (teams === null) return [];
  const seen = new Set<string>();
  let hasUnassigned = false;
  for (const entry of teams) {
    if (entry.team.archived === true && showArchived === false) continue;
    const r = entry.team.regionName;
    if (r === null || r === undefined || r.trim() === '') {
      hasUnassigned = true;
    } else {
      seen.add(r);
    }
  }
  const regions: RegionOption[] = Array.from(seen)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));
  if (hasUnassigned) {
    regions.push({ value: UNASSIGNED_REGION_VALUE, label: UNASSIGNED_REGION_LABEL });
  }
  return regions;
}

/**
 * A `{ value, label }` pair for the role filter dropdown. `value` is the
 * catalog role id, or the group's `displayName` for entra-title groups (which
 * have a null roleId); `label` is the human-readable role name.
 */
export interface RoleOption {
  value: string;
  label: string;
}

/** Key a role group for filtering: catalog id, or displayName for entra-title. */
function roleFilterValue(group: { roleId: string | null; displayName: string }): string {
  return group.roleId ?? group.displayName;
}

/**
 * Distinct role options across all teams, alphabetical by label. Used by the
 * public Team Visualization role filter (PRODOPSAI-532).
 */
export function listAvailableRoles(
  teams: readonly ITeamMembershipTreeTeam[] | null,
): RoleOption[] {
  if (teams === null) return [];
  const byValue = new Map<string, string>();
  for (const entry of teams) {
    for (const group of entry.roleGroups) {
      const value = roleFilterValue(group);
      if (!byValue.has(value)) byValue.set(value, group.displayName);
    }
  }
  return Array.from(byValue, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * Keep teams that have at least one role group matching `role` (catalog id or
 * entra-title displayName). `null` / `''` means "no filter".
 */
export function filterTeamsByRole(
  teams: readonly ITeamMembershipTreeTeam[],
  role: string | null,
): ITeamMembershipTreeTeam[] {
  if (role === null || role.trim() === '') return teams.slice();
  return teams.filter((entry) => entry.roleGroups.some((group) => roleFilterValue(group) === role));
}

export type TeamSortDirection = 'asc' | 'desc';

/** Teams sorted by name (A–Z by default). Pure — returns a new array. */
export function sortTeamsByName(
  teams: readonly ITeamMembershipTreeTeam[],
  direction: TeamSortDirection = 'asc',
): ITeamMembershipTreeTeam[] {
  const sorted = teams.slice().sort((a, b) => a.team.name.localeCompare(b.team.name));
  if (direction === 'desc') sorted.reverse();
  return sorted;
}

/**
 * Curated region display order for the public Team Overview sections
 * (PRODOPSAI-535). Global is cross-cutting and leads; the rest follow the
 * org's regional grouping. Regions not in this list render after these
 * (alphabetical), and the unassigned bucket is always last.
 */
export const REGION_DISPLAY_ORDER: readonly string[] = ['Global', 'SWE', 'UK', 'US'];

export interface RegionSection {
  /** Filter value for the region (a real region name, or the unassigned sentinel). */
  region: string;
  /** Human-readable section heading. */
  label: string;
  teams: ITeamMembershipTreeTeam[];
}

function regionBucketKey(team: ITeamMembershipTreeTeam): string {
  const r = team.team.regionName;
  return r === null || r === undefined || r.trim() === '' ? UNASSIGNED_REGION_VALUE : r;
}

/**
 * Group teams into ordered region sections for the overview layout. Input
 * order is PRESERVED within each section (so the caller's sort still applies):
 * curated regions first (REGION_DISPLAY_ORDER), then any other regions
 * alphabetically, then the unassigned bucket last. Empty sections are omitted.
 */
export function groupTeamsIntoRegionSections(
  teams: readonly ITeamMembershipTreeTeam[],
): RegionSection[] {
  const buckets = new Map<string, ITeamMembershipTreeTeam[]>();
  for (const team of teams) {
    const key = regionBucketKey(team);
    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [team]);
    } else {
      bucket.push(team);
    }
  }

  const sections: RegionSection[] = [];
  const taken = new Set<string>();

  const pushSection = (region: string, label: string): void => {
    const bucket = buckets.get(region);
    if (bucket !== undefined && bucket.length > 0) {
      sections.push({ region, label, teams: bucket });
      taken.add(region);
    }
  };

  for (const region of REGION_DISPLAY_ORDER) pushSection(region, region);

  const remaining = [...buckets.keys()]
    .filter((k) => !taken.has(k) && k !== UNASSIGNED_REGION_VALUE)
    .sort((a, b) => a.localeCompare(b));
  for (const region of remaining) pushSection(region, region);

  pushSection(UNASSIGNED_REGION_VALUE, UNASSIGNED_REGION_LABEL);
  return sections;
}
